import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getPersistedValue } from 'shared/utils/persisted-values';
import { DPAY_SESSION_TOKEN_KEY } from 'src/payments/constants';
import { tracker } from 'src/utils/analytics';
import type { AccountSignupAction, AccountSignupMethod, AccountSignupSurface } from 'src/utils/analytics/events';

export const ACCOUNT_SIGNUP_ANALYTICS_FLAG = 'fintech.accounts.signup-analytics.rollout';

export const useIsAccountSignupAnalyticsEnabled = (): boolean => {
  const flags = useFlags();
  return flags[ACCOUNT_SIGNUP_ANALYTICS_FLAG] ?? false;
};

type TrackFailedArgs = {
  errorCode?: string;
  errorMessage?: string;
};

export type UseAccountSignupAnalyticsOptions = {
  surface: AccountSignupSurface | null;
  method: AccountSignupMethod | null;
  dutchiePaySessionToken?: string;
};

export type UseAccountSignupAnalyticsReturn = {
  trackViewed: () => void;
  trackSubmitted: () => void;
  trackSucceeded: () => void;
  trackFailed: (args?: TrackFailedArgs) => void;
};

export const useAccountSignupAnalytics = ({
  surface,
  method,
  dutchiePaySessionToken,
}: UseAccountSignupAnalyticsOptions): UseAccountSignupAnalyticsReturn => {
  const isEnabled = useIsAccountSignupAnalyticsEnabled();

  const track = useCallback(
    (action: AccountSignupAction, errorFields: TrackFailedArgs = {}): void => {
      if (!isEnabled || surface === null || method === null) {
        return;
      }

      // Resolved at emit time so a token persisted after mount is picked up without subscribing
      // to storage; an explicit option always wins over the persisted fallback.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' means "absent" and must fall through to the next source; ?? would keep the empty string
      const sessionToken = dutchiePaySessionToken || getPersistedValue(DPAY_SESSION_TOKEN_KEY, '') || undefined;

      tracker.accountSignupAction({
        action,
        method,
        surface,
        ...(sessionToken !== undefined && { dutchiePaySessionToken: sessionToken }),
        ...errorFields,
      });
    },
    [dutchiePaySessionToken, isEnabled, method, surface]
  );

  const trackViewed = useCallback((): void => track('viewed'), [track]);
  const trackSubmitted = useCallback((): void => track('submitted'), [track]);
  const trackSucceeded = useCallback((): void => track('succeeded'), [track]);
  const trackFailed = useCallback(
    ({ errorCode, errorMessage }: TrackFailedArgs = {}): void => track('failed', { errorCode, errorMessage }),
    [track]
  );

  return { trackViewed, trackSubmitted, trackSucceeded, trackFailed };
};
